"use client";

import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Chip,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { documentCache } from "@/utils/documentCache";
import { Citation, getGoogleDriveWebViewLink, isGoogleDriveCitation } from "@/types/citations";
import {
  detectDocumentType,
  detectDocumentTypeFromMimeAndFilename,
  DocumentType,
} from "@/utils/fileUtils";
import { useAuthenticatedFileFetcher } from "@/hooks/chat/useAuthenticatedFileFetcher";
import { logger } from "@/telemetry";

import { PDFViewer } from "./viewers/PDFViewer";
import { DOCXViewer } from "./viewers/DOCXViewer";
import { PPTXViewer } from "./viewers/PPTXViewer";
import { ImageViewer } from "./viewers/ImageViewer";
import { TXTViewer } from "./viewers/TXTViewer";
import { CSVViewer } from "./viewers/CSVViewer";
import { ExcelViewer } from "./viewers/ExcelViewer";
import { MarkdownViewer } from "./viewers/MarkdownViewer";

/**
 * Sniff the first bytes of an ArrayBuffer to detect the actual file format,
 * regardless of what the server Content-Type header claims. This catches cases
 * where Google Workspace files are stored as PDF exports but served with the
 * original Google mimeType (e.g. application/vnd.google-apps.document).
 */
function detectTypeFromBytes(buffer: ArrayBuffer): DocumentType | null {
  if (buffer.byteLength < 8) return null;
  const header = new Uint8Array(buffer.slice(0, 8));

  // PDF: %PDF-
  if (
    header[0] === 0x25 &&
    header[1] === 0x50 &&
    header[2] === 0x44 &&
    header[3] === 0x46 &&
    header[4] === 0x2d
  )
    return "pdf";
  // DOCX/XLSX/PPTX (ZIP PK header): PK\x03\x04
  if (header[0] === 0x50 && header[1] === 0x4b && header[2] === 0x03 && header[3] === 0x04)
    return null; // Let mimeType-based detection handle which Office format

  return null;
}

export interface CitationDocumentViewerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  citation: Citation | null;
}

/**
 * Modal component for viewing citations in their original documents
 * Downloads document, renders with appropriate viewer (PDF/DOCX), and highlights citation text
 */
export const CitationDocumentViewer: React.FC<CitationDocumentViewerProps> = ({
  isOpen,
  onOpenChange,
  citation,
}) => {
  const { t } = useTranslation();
  const fileFetcher = useAuthenticatedFileFetcher();
  const [documentData, setDocumentData] = React.useState<ArrayBuffer | null>(null);
  const [documentType, setDocumentType] = React.useState<DocumentType>("unknown");

  /**
   * Detect document type using filename, mimeType from citation metadata, and Google Drive heuristics.
   * Google Workspace files are exported as PDF during backfill, so they render via PDFViewer.
   * Falls back to markdown viewer if PDF export was unavailable.
   */
  const detectType = React.useCallback(
    (filename: string, mimeType?: string | null, data?: ArrayBuffer | null): DocumentType => {
      // Check actual bytes first — most reliable for PDFs served with wrong Content-Type
      if (data) {
        const byBytes = detectTypeFromBytes(data);

        if (byBytes) return byBytes;
      }

      // Try extension-based detection
      const byExtension = detectDocumentType(filename);

      if (byExtension !== "unknown") return byExtension;

      // Try mimeType-based detection (handles Google Workspace → "markdown", standard types, etc.)
      if (mimeType) {
        const byMime = detectDocumentTypeFromMimeAndFilename(mimeType, filename);

        if (byMime && byMime !== "unknown") return byMime;
      }

      // For Google Drive citations without recognisable extension/mime,
      // default to markdown as a safe fallback (normally these are exported as PDF)
      if (citation && isGoogleDriveCitation(citation)) return "markdown";

      return "unknown";
    },
    [citation],
  );

  // Download document when citation changes
  // For Google Drive files, try PDF preview first for better rendering + highlighting
  React.useEffect(() => {
    if (!citation || !isOpen) return;

    const downloadDocument = async () => {
      // Check cache first
      const cacheKey = citation.document_id;
      const cached = documentCache.get(cacheKey);

      if (cached) {
        logger.info("Using cached document", { documentId: citation.document_id });
        const arrayBuffer = new ArrayBuffer(cached.data.byteLength);
        const view = new Uint8Array(arrayBuffer);

        view.set(cached.data);
        setDocumentData(arrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            cached.mimeType || citation.metadata?.mimeType,
            arrayBuffer,
          ),
        );

        return;
      }

      // Download the file (Google Drive files are already stored as PDF by backfill)
      logger.info("Downloading document", { documentId: citation.document_id });
      await fileFetcher.fetchFile(citation.document_id, citation.filename, {
        asArrayBuffer: true,
      });
    };

    downloadDocument();
    // TODO: Stabilise fileFetcher ref so the full dependency array can be used
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fileFetcher is an unstable object ref; citation and isOpen are the real triggers
  }, [citation, isOpen]);

  // Handle fetched data: cache and set state
  React.useEffect(() => {
    if (fileFetcher.data.arrayBuffer && citation) {
      // IMPORTANT: Cache FIRST before PDF.js can detach the ArrayBuffer
      // The cache.set() converts it to Uint8Array immediately
      documentCache.set(
        citation.document_id,
        fileFetcher.data.arrayBuffer,
        undefined,
        fileFetcher.data.mimeType || undefined,
      );

      // Now get it back from cache as a fresh ArrayBuffer
      const cachedDoc = documentCache.get(citation.document_id);

      if (cachedDoc) {
        const freshArrayBuffer = new ArrayBuffer(cachedDoc.data.byteLength);
        const view = new Uint8Array(freshArrayBuffer);

        view.set(cachedDoc.data);
        setDocumentData(freshArrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            fileFetcher.data.mimeType || citation.metadata?.mimeType,
            freshArrayBuffer,
          ),
        );

        logger.info("✅ Document downloaded", {
          sizeKB: (fileFetcher.data.arrayBuffer.byteLength / 1024).toFixed(1),
        });
      } else {
        // Fallback (should never happen)
        setDocumentData(fileFetcher.data.arrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            fileFetcher.data.mimeType || citation.metadata?.mimeType,
            fileFetcher.data.arrayBuffer,
          ),
        );
      }
    }
  }, [fileFetcher.data.arrayBuffer, citation, fileFetcher.data.mimeType, detectType]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setDocumentData(null);
      fileFetcher.cleanup();
    }
    // TODO: Stabilise fileFetcher ref so the full dependency array can be used
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fileFetcher is an unstable object ref; isOpen is the real trigger
  }, [isOpen]);

  const renderViewer = () => {
    if (fileFetcher.loading) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label={t("Loading document...") || "Loading document..."} />
        </div>
      );
    }

    if (fileFetcher.error) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <Icon className="text-danger-500" icon="solar:danger-circle-linear" width={48} />
          <p className="text-danger-500">
            {t("Failed to load document. Please try again.") || fileFetcher.error}
          </p>
          {citation && (
            <div className="bg-default-100 dark:bg-default-100/50 p-4 rounded-medium max-w-2xl">
              <p className="text-sm font-medium mb-2 text-default-800 dark:text-white">
                Citation text:
              </p>
              <p className="text-xs text-default-600 dark:text-default-300">{citation.text}</p>
            </div>
          )}
        </div>
      );
    }

    if (!documentData || !citation) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-default-500 dark:text-default-400">No document to display</p>
        </div>
      );
    }

    if (documentType === "pdf") {
      return <PDFViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "docx") {
      return <DOCXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "pptx") {
      return <PPTXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "image") {
      return <ImageViewer documentData={documentData} filename={citation.filename} />;
    }

    if (documentType === "txt") {
      return <TXTViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "markdown") {
      return <MarkdownViewer documentData={documentData} />;
    }

    if (documentType === "csv") {
      return (
        <CSVViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    if (documentType === "excel") {
      return (
        <ExcelViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    // Check if this is a Google Drive file with a web view link
    const isGoogleFile = isGoogleDriveCitation(citation);
    const webViewLink = getGoogleDriveWebViewLink(citation);

    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Icon className="text-warning-500" icon="solar:file-text-linear" width={48} />
        <p className="text-default-600 dark:text-default-300">
          {isGoogleFile ? "Google Drive File" : "Unsupported document type"}
        </p>
        {webViewLink ? (
          <>
            <a
              className="text-primary hover:underline flex items-center gap-2"
              href={webViewLink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon icon="logos:google-drive" width={20} />
              Open in Google Drive
            </a>
            {citation?.text && (
              <div className="bg-default-100 dark:bg-default-100/50 p-4 rounded-medium max-w-2xl mt-4">
                <p className="text-sm font-medium mb-2 text-default-800 dark:text-white">
                  Citation text:
                </p>
                <p className="text-xs text-default-600 dark:text-default-300 whitespace-pre-wrap">
                  {citation.text}
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-default-500 dark:text-default-400">
            Only PDF, DOCX, PPTX, TXT, Markdown, CSV/TSV, Excel, and image files are supported
          </p>
        )}
      </div>
    );
  };

  return (
    <Modal
      aria-labelledby="citation-modal-title"
      backdrop="blur"
      data-testid="citation-document-viewer-modal"
      isOpen={isOpen}
      size="5xl"
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader
              className="flex items-center gap-3 border-b border-divider pb-3"
              id="citation-modal-title"
            >
              <Icon className="text-primary" icon="solar:book-bookmark-linear" width={24} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {citation && (
                    <Chip color="primary" size="sm" variant="flat">
                      {citation.label}
                    </Chip>
                  )}
                  <span className="text-base font-semibold truncate">
                    {citation?.filename || "Citation"}
                  </span>
                </div>
              </div>
            </ModalHeader>

            <ModalBody aria-label="Document viewer" className="p-0" role="document">
              {renderViewer()}
            </ModalBody>

            <ModalFooter className="border-t border-divider">
              <Button
                color="default"
                data-testid="citation-modal-close-button"
                variant="light"
                onPress={onClose}
              >
                {t("Close") || "Close"}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CitationDocumentViewer;
